import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { formatCurrency } from '../../../utils/helper';
import { hasPermission } from '../../../utils/permissions';
import { todayInUAEString } from '../../../utils/timezone';
import { formatDiscount } from './List';

// Dates are whole UAE days (see promoCodeController.createOrUpdate).
const formatDay = (date) => (date ? moment(todayInUAEString(new Date(date))).format('DD MMM YYYY') : null);

const validity = (promo) => {
    const now = moment();
    if (!promo.status) return { label: 'Inactive', className: 'badge-light-secondary' };
    if (promo.start_date && now.isBefore(promo.start_date)) return { label: 'Scheduled', className: 'badge-light-info' };
    if (promo.expiry_date && now.isAfter(promo.expiry_date)) return { label: 'Expired', className: 'badge-light-danger' };
    return { label: 'Running', className: 'badge-light-success' };
};

const InfoRow = ({ label, children }) => (
    <div className="d-flex justify-content-between align-items-start py-3 border-bottom border-gray-200 gap-4">
        <span className="text-gray-500 fw-semibold">{label}</span>
        <span className="text-dark fw-semibold text-end">{children}</span>
    </div>
);

const StatCard = ({ label, value, icon, color, note }) => (
    <div className="col-6 col-md">
        <div className="bg-white p-4 rounded-4 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h6 className="text-gray-600 fw-semibold mb-0">{label}</h6>
                <i className={`bi ${icon} fs-2 ${color}`}></i>
            </div>
            <h2 className="fw-bold text-dark mb-0">{value}</h2>
            {note && <div className="text-muted fs-7 mt-1">{note}</div>}
        </div>
    </div>
);

// One promo code: its settings, redemption stats, the users it's assigned to
// (with how many times each has used it), and the full usage history.
const Details = () => {
    const { id } = useParams();
    const [promo, setPromo] = useState(null);
    const [stats, setStats] = useState(null);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [loadingPromo, setLoadingPromo] = useState(true);

    const [usages, setUsages] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [loadingUsages, setLoadingUsages] = useState(false);

    useEffect(() => {
        setLoadingPromo(true);
        ApiService.request({ method: 'GET', url: `admin/promo-codes/${id}` })
            .then((response) => {
                const data = response.data;
                if (data.status) {
                    setPromo(data.data);
                    setStats(data.stats);
                    setAssignedUsers(data.assigned_users || []);
                } else {
                    toast.error(data.message);
                }
            })
            .catch(() => {})
            .finally(() => setLoadingPromo(false));
    }, [id]);

    useEffect(() => {
        setLoadingUsages(true);
        ApiService.request({ method: 'GET', url: `admin/promo-codes/${id}/usages`, data: { page } })
            .then((response) => {
                const data = response.data;
                if (data.status) {
                    setUsages(data.data);
                    setPagination(data.pagination);
                }
            })
            .catch(() => {})
            .finally(() => setLoadingUsages(false));
    }, [id, page]);

    const perUserLabel = (p) =>
        p.usage_type === 'single'
            ? 'Once per user'
            : p.per_user_limit != null ? `${p.per_user_limit} times per user` : 'Unlimited per user';

    const state = promo ? validity(promo) : null;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack flex-wrap gap-3">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex align-items-center gap-3 text-dark fw-bold fs-3 my-0">
                            {promo ? promo.code : 'Promo Code'}
                            {state && <span className={`badge ${state.className} fs-7`}>{state.label}</span>}
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/promo-codes" className="text-muted text-hover-primary">Promo Codes</Link>
                            </li>
                        </ul>
                    </div>
                    {promo && hasPermission('promo-codes.save') && (
                        <Link to={`/admin/promo-codes/create/${promo._id}`} className="btn btn-sm btn-light-primary">
                            <i className="bi bi-pencil-fill fs-7 me-1"></i> Edit
                        </Link>
                    )}
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    {loadingPromo && !promo ? (
                        <Loader text="Loading" size="30" className="text-center" />
                    ) : !promo ? (
                        <div className="card"><div className="card-body text-center text-muted">Promo code not found.</div></div>
                    ) : (
                        <>
                            {/* Stats */}
                            <div className="row g-3 mb-6">
                                <StatCard
                                    label="Times Used"
                                    value={`${stats?.used_count ?? 0}${promo.total_usage_limit != null ? ` / ${promo.total_usage_limit}` : ''}`}
                                    note={stats?.remaining != null ? `${stats.remaining} remaining` : 'No overall limit'}
                                    icon="bi-ticket-perforated"
                                    color="text-primary"
                                />
                                <StatCard label="Unique Users" value={stats?.unique_users ?? 0} icon="bi-people" color="text-info" />
                                <StatCard label="Total Discount Given" value={formatCurrency(stats?.total_discount ?? 0)} icon="bi-tag" color="text-success" />
                                <StatCard label="Revenue With Code" value={formatCurrency(stats?.total_revenue ?? 0)} icon="bi-cash-stack" color="text-warning" />
                                {stats?.reverted_count > 0 && (
                                    <StatCard
                                        label="Reverted"
                                        value={stats.reverted_count}
                                        note="Rejected memberships"
                                        icon="bi-arrow-counterclockwise"
                                        color="text-gray-500"
                                    />
                                )}
                            </div>

                            <div className="row g-6 mb-6">
                                {/* Settings */}
                                <div className="col-lg-5">
                                    <div className="card h-100">
                                        <div className="card-header border-0 pt-6">
                                            <h3 className="card-title fw-bold text-dark">Details</h3>
                                        </div>
                                        <div className="card-body pt-0">
                                            {promo.title && <InfoRow label="Title">{promo.title}</InfoRow>}
                                            {promo.description && <InfoRow label="Description">{promo.description}</InfoRow>}
                                            <InfoRow label="Discount">{formatDiscount(promo)}</InfoRow>
                                            <InfoRow label="Min Order">
                                                {promo.min_order_amount != null ? formatCurrency(promo.min_order_amount) : 'Any amount'}
                                            </InfoRow>
                                            <InfoRow label="Applies To">
                                                {promo.plan_ids?.length
                                                    ? promo.plan_ids.map((plan) => (
                                                        <span key={plan._id} className="badge badge-light-primary ms-1 mb-1">{plan.title}</span>
                                                    ))
                                                    : 'All plans'}
                                            </InfoRow>
                                            <InfoRow label="Who Can Use">
                                                {promo.user_scope === 'specific'
                                                    ? `${promo.user_ids?.length || 0} specific user${promo.user_ids?.length === 1 ? '' : 's'}`
                                                    : 'Anyone'}
                                            </InfoRow>
                                            <InfoRow label="Usage Per User">{perUserLabel(promo)}</InfoRow>
                                            <InfoRow label="Total Uses">
                                                {promo.total_usage_limit != null ? promo.total_usage_limit : 'Unlimited'}
                                            </InfoRow>
                                            <InfoRow label="Starts">{formatDay(promo.start_date) || 'Immediately'}</InfoRow>
                                            <InfoRow label="Expires">{formatDay(promo.expiry_date) || 'Never'}</InfoRow>
                                            <InfoRow label="Status">
                                                <span className={`badge ${promo.status ? 'badge-light-success' : 'badge-light-secondary'}`}>
                                                    {promo.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </InfoRow>
                                            <InfoRow label="Created">{moment(promo.createdAt).format('DD MMM YYYY, hh:mm A')}</InfoRow>
                                        </div>
                                    </div>
                                </div>

                                {/* Assigned users */}
                                <div className="col-lg-7">
                                    <div className="card h-100">
                                        <div className="card-header border-0 pt-6">
                                            <h3 className="card-title fw-bold text-dark">Assigned Users</h3>
                                        </div>
                                        <div className="card-body pt-0">
                                            {promo.user_scope !== 'specific' ? (
                                                <div className="text-muted py-6 text-center">
                                                    <i className="bi bi-globe2 fs-2x d-block mb-2"></i>
                                                    This code is open to anyone who enters it. It isn't listed in the app.
                                                </div>
                                            ) : assignedUsers.length === 0 ? (
                                                <div className="text-muted py-6 text-center">No users assigned.</div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table align-middle table-row-dashed fs-6 gy-3 mb-0">
                                                        <thead>
                                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                                <th>User</th>
                                                                <th>Used</th>
                                                                <th>Last Used</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="fw-semibold text-gray-600">
                                                            {assignedUsers.map((user) => {
                                                                const exhausted = user.per_user_limit != null && user.used_count >= user.per_user_limit;
                                                                return (
                                                                    <tr key={user._id}>
                                                                        <td>
                                                                            <Link to={`/admin/users/${user._id}`} className="text-dark fw-bold text-hover-primary">
                                                                                {user.name || 'Unnamed user'}
                                                                            </Link>
                                                                            <div className="text-muted fs-7">{user.email || user.phone || ''}</div>
                                                                        </td>
                                                                        <td>
                                                                            {user.used_count}
                                                                            {user.per_user_limit != null ? ` / ${user.per_user_limit}` : ''}
                                                                        </td>
                                                                        <td>{user.last_used_at ? moment(user.last_used_at).format('DD MMM YYYY') : '—'}</td>
                                                                        <td>
                                                                            {exhausted ? (
                                                                                <span className="badge badge-light-secondary text-dark">Used up</span>
                                                                            ) : user.used_count > 0 ? (
                                                                                <span className="badge badge-light-primary">Used</span>
                                                                            ) : (
                                                                                <span className="badge badge-light-success">Available</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Usage history */}
                            <div className="card">
                                <div className="card-header border-0 pt-6">
                                    <h3 className="card-title fw-bold text-dark">Usage History</h3>
                                </div>
                                <div className="card-body pt-0">
                                    <div className="table-responsive">
                                        <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                            <thead>
                                                <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                    <th>User</th>
                                                    <th>Plan</th>
                                                    <th>Original</th>
                                                    <th>Discount</th>
                                                    <th>Paid</th>
                                                    <th>Transaction</th>
                                                    <th>Date</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="fw-semibold text-gray-600 fs-6">
                                                {usages.length > 0 ? usages.map((usage) => (
                                                    <tr key={usage._id}>
                                                        <td>
                                                            {usage.user_id ? (
                                                                <Link to={`/admin/users/${usage.user_id._id}`} className="text-dark fw-bold text-hover-primary">
                                                                    {usage.user_id.name || 'Unnamed user'}
                                                                </Link>
                                                            ) : '—'}
                                                            <div className="text-muted fs-7">{usage.user_id?.email || usage.user_id?.phone || ''}</div>
                                                        </td>
                                                        <td>{usage.user_membership_id?.plan_id?.title || '—'}</td>
                                                        <td>{formatCurrency(usage.original_amount)}</td>
                                                        <td className="text-success">− {formatCurrency(usage.discount_amount)}</td>
                                                        <td className="text-dark fw-bold">{formatCurrency(usage.final_amount)}</td>
                                                        <td className="fs-7 text-break">{usage.transaction_id || '—'}</td>
                                                        <td>{moment(usage.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                                                        <td>
                                                            {usage.status === 'Applied' ? (
                                                                <span className="badge badge-light-success">Applied</span>
                                                            ) : (
                                                                <span className="badge badge-light-secondary" title="Membership request was rejected, so this use was given back">
                                                                    Reverted
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-dark text-center mt-2">No usage yet.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {loadingUsages && <Loader text="Loading" size="30" className="text-center" />}
                                        {pagination.total_pages > 1 && (
                                            <Paginator
                                                page={pagination.current_page}
                                                rows={pagination.per_page}
                                                totalRecords={pagination.total_records}
                                                onPageChange={setPage}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Details;
